import React, {useEffect, useState} from "react";
import {Button, Col, Form, Row, Table} from "react-bootstrap";
import {FormLabelRequiredAsterisk} from "Components/Form/FormLabelRequiredAsterisk";
import {EnrollmentResponse} from "../../Api/Query/Enrollments";
import Toggle from "react-toggle";
import {ActivitySelect} from "../../Components/Activities/ActivitySelect";
import {LabelValue} from "../../Model/Shared/IdName";
import {ActivitySessionSelect} from "../../Components/Activities/ActivitySessionSelect";

interface Props {
    onSave: (formData: { [key: string]: any }) => void,
    entity?: EnrollmentResponse
}

export const EnrollmentForm: React.FC<Props> = (props) => {

    const initDefaultData = () => {
        return {
            name: props.entity?.name ?? '',
            price: props.entity?.formattedPrice ?? '',
            enabled: props.entity?.enabled ?? false,
            activitySessions: props.entity?.activitySessions ?? []
        }
    }

    const [activity, setActivity] = useState<{ label: string, value: string}|null>(null)
    const [activitySessions, setActivitySessions] = useState<{ label: string, value: string}[]>([])
    const [formData, setFormData] = useState<{ [key: string]: any }>(initDefaultData())
    const [formErrors, setFormErrors] = useState<{ [key: string]: any }>({
        name: false,
        price: false,
    })
    const [submited, setSubmited] = useState<boolean>(false)

    const validate = (): boolean => {
        let errors: number = 0
        let currentFormErrors = {}

        if (!formData.name) {
            currentFormErrors = {
                ...currentFormErrors,
                name: true
            }
            errors++
        }

        if (!formData.price) {
            currentFormErrors = {
                ...currentFormErrors,
                price: true
            }
            errors++
        }

        if (errors === 0) {
            return true
        }

        setFormErrors(currentFormErrors)
        return false
    }

    const save = () => {
        if (!submited) {
            setSubmited(true)
        }

        if (!validate()) {
            return
        }

        props.onSave(formData)
    }

    const addActivitySession = () => {
        if (!activitySessions || activitySessions.length === 0) {
            return
        }

        const newActivitySessions = [...formData.activitySessions]
        const duplicates: string[] = []

        activitySessions.forEach((session) => {
            const exists = formData.activitySessions.find((item: LabelValue) => item.value === session.value)
            if (exists) {
                duplicates.push(session.label)
            } else {
                newActivitySessions.push({...session, activity: activity})
            }
        })

        if (duplicates.length > 0) {
            alert(`Las siguientes modalidades ya están vinculadas: ${duplicates.join(', ')}`)
        }

        setFormData({
            ...formData,
            activitySessions: newActivitySessions
        })
        setActivitySessions([])
    }

    useEffect(() => {
        setFormData(initDefaultData())
    }, [props.entity]);

    useEffect(() => {
        if (submited) {
            validate()
        }
    }, [formData]);

    return (
        <>
            <Form className={"mt-4"}>
                <div className={"row"}>
                    <Col xs={12} sm={6} md={6}>
                        <Form.Group controlId={"queryFilter"}>
                            <Form.Label>
                                Nombre
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    name: e.target.value
                                })}
                                required
                                className={formErrors.name ? 'is-invalid' : ''}
                                value={formData.name ?? ''}
                            />
                            {formErrors.name && <p className={"text-danger pt-2"}>El campo es obligatorio</p>}
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm={6} md={6}>
                        <Form.Group controlId={"queryFilter"}>
                            <Form.Label>
                                Precio
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="number"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    price: e.target.value
                                })}
                                required
                                className={formErrors.price ? 'is-invalid' : ''}
                                value={formData.price ?? ''}
                            />
                            {formErrors.price && <p className={"text-danger pt-2"}>El campo es obligatorio</p>}
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6}>
                        <Form.Group
                            controlId={"enabledGroup"}
                            className={"mt-4"}
                        >
                            <label
                                htmlFor={"enabled"}
                                className={"d-block mb-2 fw-bold pointer"}
                            >
                                Habilitado
                            </label>
                            <Toggle
                                id={"enabled"}
                                checked={formData.enabled}
                                onChange={() => setFormData({...formData, enabled: !formData.enabled})}
                            />
                        </Form.Group>
                    </Col>
                </div>
                <div className={"row mt-4"}>
                    <h4 className={"mt-2"}>Actividades</h4>
                    <Col xs={12} lg={10}>
                        <Form.Group
                            className={"mt-4"}
                        >
                            <label
                                htmlFor={"addActivity"}
                                className={"d-block mb-2 fw-bold pointer"}
                            >
                                Añadir actividad
                            </label>
                            <div className={"d-flex align-items-center"}>
                                <ActivitySelect
                                    onChange={(val) => { setActivity(val); setActivitySessions([]); }}
                                    classes={"w-100"}
                                    placeholder={"Selecciona una actividad"}
                                />
                                &nbsp;&nbsp;

                                {
                                    activity &&
                                    <ActivitySessionSelect
                                        activityId={activity?.value ?? ''}
                                        onChange={(val) => setActivitySessions(Array.isArray(val) ? val : val ? [val] : [])}
                                        classes={"w-100"}
                                        placeholder={"Selecciona una o varias modalidades"}
                                        isMulti={true}
                                        value={activitySessions}
                                    />
                                }

                                <Button
                                    className={"purple-btn ms-3"}
                                    onClick={addActivitySession}
                                >
                                    Añadir
                                </Button>
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12}>
                        <table className={"table table-bordered redTable mt-4"}>
                            <thead>
                            <tr>
                                <th>Actividad</th>
                                <th>Modalidad</th>
                                <th className={"text-center"}>Acciones</th>
                            </tr>
                            </thead>
                            <tbody>
                            {
                                formData.activitySessions.map((item: any, key: number) => {
                                    return (
                                        <tr key={key}>
                                            <td width={"40%"}>
                                                {item.activity.label}
                                            </td>
                                            <td width={"50%"}>
                                                {item.label}
                                            </td>
                                            <td className={"text-center"}>
                                                <Button
                                                    className={"btn btn-danger btn-sm"}
                                                    onClick={() => {
                                                        const newActivitySessions = formData.activitySessions.filter((activitySession: any) => activitySession.value !== item.value)
                                                        setFormData({
                                                            ...formData,
                                                            activitySessions: newActivitySessions
                                                        })
                                                    }}
                                                >
                                                    Eliminar
                                                </Button>
                                            </td>
                                        </tr>
                                    )
                                })
                            }

                            {
                                formData.activitySessions.length === 0 &&
                                <tr>
                                    <td colSpan={2}>No hay modalidades vinculadas para esta matricula.</td>
                                </tr>
                            }
                            </tbody>
                        </table>
                    </Col>

                </div>
            </Form>

            <Row style={{"paddingTop": "50px"}}>
                <Col xs={12} sm={4}>
                    <Button
                        className={"purple-btn mt-4"}
                        onClick={() => save()}
                    >
                        Guardar
                    </Button>
                </Col>
            </Row>
        </>
    )
}
